import React from 'react'
import { X, Crown, Sparkles, BarChart3, Zap } from 'lucide-react'

export interface UpgradeReason {
  type: 'premium_blocks' | 'animations' | 'demo_limit'
  blocks?: string[]
  limit?: number
  current?: number
}

const BLOCK_LABELS: Record<string, string> = {
  chart: 'Chart',
  slider: 'Slider',
  chat_bubble: 'Chat Bubble',
  codeblock: 'Code Block',
  poll: 'Poll',
  quiz: 'Quiz',
  compare: 'Before/After',
  toggle: 'Toggle',
  dropdown: 'Dropdown',
  tabs: 'Tabs',
  number_input: 'Number Input',
}

interface UpgradeModalProps {
  reasons: UpgradeReason[]
  onClose: () => void
}

export default function UpgradeModal({ reasons, onClose }: UpgradeModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-amber-400" />
            <h2 className="text-lg font-bold text-slate-900">Upgrade to Pro</h2>
          </div>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-600 rounded">
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="text-sm text-slate-600 mb-5">
          Your demo uses Pro features. Upgrade to publish it.
        </p>

        <div className="space-y-3 mb-6">
          {reasons.map((reason, i) => (
            <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 border border-amber-100">
              {reason.type === 'premium_blocks' && (
                <>
                  <BarChart3 className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-slate-800">Premium blocks used</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {reason.blocks?.map((b) => BLOCK_LABELS[b] || b).join(', ')}
                    </p>
                  </div>
                </>
              )}
              {reason.type === 'animations' && (
                <>
                  <Sparkles className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-slate-800">Animations</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Block animations are a Pro feature
                    </p>
                  </div>
                </>
              )}
              {reason.type === 'demo_limit' && (
                <>
                  <Zap className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-slate-800">Demo limit reached</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Free plan allows {reason.limit} published demos. You have {reason.current}.
                    </p>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-all"
          >
            Maybe later
          </button>
          <form action="/checkout" method="post">
            <input type="hidden" name="authenticity_token" value={document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || ''} />
            <input type="hidden" name="price_id" value={document.getElementById('editor-root')?.dataset.stripeCreatorPriceId || ''} />
            <button
              type="submit"
              className="flex items-center gap-2 bg-[#5048e5] text-white px-5 py-2.5 rounded-lg text-sm font-bold shadow-sm shadow-[#5048e5]/25 hover:bg-[#5048e5]/90 transition-all cursor-pointer"
            >
              <Crown className="h-4 w-4" />
              Upgrade — $9/mo
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}
